import clsx from 'clsx';
import { Focus } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { useOSStore } from '../store/useOSStore';
import { formatCurrency, formatPct } from '../utils/format';
import { DensityToggle } from './DensityToggle';

// ── Aura metric atom ──────────────────────────────────────────────────────────

interface AuraMetric {
  label: string;
  value: string;
  valueColor?: string;
  dotColor?: string;
  pulse?: boolean;
  separator?: boolean;
}

function AuraMetric({ label, value, valueColor, dotColor, pulse, separator }: AuraMetric) {
  return (
    <>
      {separator && <div className="w-px h-3 bg-surface-4 flex-shrink-0" />}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {dotColor && (
          <span
            className={clsx(
              'w-1 h-1 rounded-full flex-shrink-0',
              dotColor,
              pulse && 'animate-pulse-slow',
            )}
          />
        )}
        <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-gray-600 leading-none select-none">
          {label}
        </span>
        <span
          className={clsx(
            'text-[10px] font-bold font-mono tabular-nums leading-none',
            valueColor ?? 'text-gray-300',
            pulse && 'animate-pulse-slow',
          )}
        >
          {value}
        </span>
      </div>
    </>
  );
}

// ── PulseAura ─────────────────────────────────────────────────────────────────

export function PulseAura() {
  const {
    recommendations,
    bets,
    bankrollSettings,
    exposure,
    connectionStatus,
    games,
    signals,
    agentActivity,
  } = useAppStore();
  const { focusMode, toggleFocusMode } = useOSStore();

  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting';

  const bankrollConfigured = bankrollSettings !== null && (bankrollSettings.currentBankroll ?? 0) > 0;
  const isCalibrating = !bankrollConfigured && recommendations.length === 0;

  const liveMLB = games.filter((g) => g.game.status.abstractGameState === 'Live').length;

  // Per-sport recent agent activity (last 5 min) — proxy for live/active analysis
  const recentByLeague = (id: string) =>
    agentActivity.filter(
      (e) => e.leagueId === id && Date.now() - new Date(e.timestamp).getTime() < 5 * 60_000,
    ).length;
  const liveWNBA = recentByLeague('wnba');
  const liveNBA = recentByLeague('nba');

  // Total configured agents: 9 MLB + 8 WNBA + 8 NBA
  const TOTAL_AGENTS = 25;

  const betNowCount = recommendations.filter((r) => r.timingRecommendation === 'BET_NOW').length;
  const qualifiedCount = recommendations.filter(
    (r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  ).length;

  const openBets = bets.filter((b) => b.status === 'placed' || b.status === 'live').length;

  const avgConf =
    recommendations.length > 0
      ? recommendations.reduce((s, r) => s + (r.confidence ?? 0), 0) / recommendations.length
      : null;

  const confColor =
    avgConf === null
      ? 'text-gray-600'
      : avgConf >= 0.70
        ? 'text-win'
        : avgConf >= 0.55
          ? 'text-warn'
          : 'text-gray-400';

  const bankroll = bankrollSettings?.currentBankroll;
  const totalExp = exposure?.totalOpen;

  const activeSignals = signals.filter((s) => s.isActive).length;
  const recentAgents = agentActivity.filter(
    (e) => Date.now() - new Date(e.timestamp).getTime() < 5 * 60 * 1000,
  ).length;

  return (
    <div
      className={clsx(
        'flex items-center gap-3 px-3 h-8 border-b flex-shrink-0 overflow-x-auto scrollbar-hide',
        'bg-surface-1/80 backdrop-blur-sm',
        isConnected ? 'border-surface-4' : 'border-surface-3',
      )}
    >
      {/* System heartbeat */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span
          className={clsx(
            'relative flex h-1.5 w-1.5 flex-shrink-0',
            !isConnected && !isConnecting && 'hidden',
          )}
        >
          {isConnected && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
          )}
          <span
            className={clsx(
              'relative inline-flex rounded-full h-1.5 w-1.5',
              isConnected ? 'bg-purple' : isConnecting ? 'bg-warn' : 'bg-loss',
            )}
          />
        </span>
        <span
          className={clsx(
            'text-[9px] font-bold uppercase tracking-[0.15em] leading-none select-none',
            isConnected ? 'text-purple/70' : isConnecting ? 'text-warn/70' : 'text-loss/70',
          )}
        >
          {isConnected ? 'PULSE LIVE' : isConnecting ? 'SYNCING' : 'OFFLINE'}
        </span>
      </div>

      <div className="w-px h-3 bg-surface-4 flex-shrink-0" />

      {/* Agent activity */}
      <AuraMetric
        label="Agents"
        value={`${TOTAL_AGENTS} Active`}
        valueColor="text-purple-bright"
        dotColor="bg-purple"
        pulse={isConnected}
      />

      {recentAgents > 0 && (
        <AuraMetric
          label="Recent"
          value={`${recentAgents} events`}
          valueColor="text-purple/80"
          separator
        />
      )}

      {/* Live games — MLB from games store; WNBA/NBA from recent agent activity */}
      <AuraMetric
        label="MLB Live"
        value={liveMLB > 0 ? `${liveMLB}` : '—'}
        valueColor={liveMLB > 0 ? 'text-win' : 'text-gray-600'}
        dotColor={liveMLB > 0 ? 'bg-win' : undefined}
        pulse={liveMLB > 0}
        separator
      />
      <AuraMetric
        label="WNBA Live"
        value={liveWNBA > 0 ? `${liveWNBA}` : '—'}
        valueColor={liveWNBA > 0 ? 'text-warn' : 'text-gray-600'}
        dotColor={liveWNBA > 0 ? 'bg-warn' : undefined}
        pulse={liveWNBA > 0}
        separator
      />
      <AuraMetric
        label="NBA Live"
        value={liveNBA > 0 ? `${liveNBA}` : '—'}
        valueColor={liveNBA > 0 ? 'text-emerald-400' : 'text-gray-600'}
        dotColor={liveNBA > 0 ? 'bg-emerald-400' : undefined}
        pulse={liveNBA > 0}
        separator
      />

      {/* Opportunities */}
      {betNowCount > 0 && (
        <AuraMetric
          label="BET NOW"
          value={String(betNowCount)}
          valueColor="text-gold-bright"
          dotColor="bg-gold"
          pulse
          separator
        />
      )}

      <AuraMetric
        label="Qualified"
        value={qualifiedCount > 0 ? String(qualifiedCount) : '—'}
        valueColor={qualifiedCount > 0 ? 'text-brand' : 'text-gray-600'}
        separator
      />

      {/* Open bets */}
      {openBets > 0 && (
        <AuraMetric
          label="Open Bets"
          value={String(openBets)}
          valueColor="text-info"
          separator
        />
      )}

      {/* Signals */}
      {activeSignals > 0 && (
        <AuraMetric
          label="Signals"
          value={String(activeSignals)}
          valueColor="text-warn"
          dotColor="bg-warn"
          separator
        />
      )}

      {/* Confidence */}
      <AuraMetric
        label="Avg Conf"
        value={avgConf !== null ? formatPct(avgConf) : '—'}
        valueColor={confColor}
        separator
      />

      {/* Exposure / Bankroll */}
      {totalExp != null && totalExp > 0 && (
        <AuraMetric
          label="Exposure"
          value={formatCurrency(totalExp)}
          valueColor="text-warn"
          separator
        />
      )}

      {bankroll != null && (
        <AuraMetric
          label="Bankroll"
          value={formatCurrency(bankroll)}
          valueColor="text-gray-300"
          separator
        />
      )}

      {/* Calibrating state — shown when bankroll not configured and no recs yet */}
      {isCalibrating && (
        <>
          <div className="w-px h-3 bg-surface-4 flex-shrink-0" />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-warn opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-warn" />
            </span>
            <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-warn/70 leading-none select-none">
              Intelligence Calibrating
            </span>
          </div>
        </>
      )}

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* Right-side controls */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Density toggle */}
        <DensityToggle />

        {/* Focus mode toggle */}
        <button
          onClick={toggleFocusMode}
          title={focusMode ? 'Exit focus mode (F)' : 'Enter focus mode (F)'}
          className={clsx(
            'hidden md:flex items-center justify-center w-5 h-5 rounded transition-all',
            focusMode
              ? 'text-brand bg-brand/15'
              : 'text-gray-600 hover:text-gray-400',
          )}
        >
          <Focus className="w-3 h-3" />
        </button>

        {/* Timestamp */}
        <span className="text-[9px] text-gray-700 font-mono tabular-nums select-none">
          {new Date().toLocaleTimeString('en-US', {
            timeZone: 'America/Los_Angeles',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })}{' '}
          PT
        </span>
      </div>
    </div>
  );
}
